import React, { FC, useEffect, useState, useCallback, useMemo } from "react";
import { Dropdown, Tag, Row, Col } from "antd";
import { DownOutlined } from "@ant-design/icons";
import join from "lodash/join";
import uniqBy from "lodash/uniqBy";
import isEqual from "lodash/isEqual";
import ArrayTable from "./ArrayTable";
import "./ArraySelect.css";
// import SingleModel from './SingleModel'
import { helper } from "@src/controls/controlHelper";
import { IS_DEBUG } from "@src/constants/constants";

export interface ArraySelectProps {
  type?: "radio" | "checkbox";
  schema: any;
  disabled?: boolean;
  invalid?: boolean;
  value?: any;
  onChange?: (val: any) => void;
  placeholder?: string;
  style?: React.CSSProperties;
  className?: string;
  [key: string]: any;
}

const ArraySelect: FC<ArraySelectProps> = (props: ArraySelectProps) => {
  const {
    type = "checkbox",
    schema,
    value: initValue,
    onChange: superChange,
    placeholder = "",
    ...rest
  } = props;
  const [pageInfo, setPageInfo] = useState<any>(null);
  const [embeds, setEmbeds] = useState<any>([]);
  const [displayObj, setDisplayObj] = useState<{
    names: Array<any>;
    display: any;
  }>({
    names: [],
    display: "",
  });
  // const [pageId] = useState<number>(props.schema.pageId || 0); // Not used anymore
  const [visible, setVisible] = useState(false);
  const lastFetchRef = React.useRef<string>("");
  const [value, setValue] = useState(() => {
    if (initValue) {
      if (Array.isArray(initValue)) {
        return initValue;
      } else {
        return [initValue];
      }
    }
    return [];
    /* if (type == 'checkbox') {
      return initValue && Array.isArray(initValue) ? initValue : []
    } else {
      return initValue && typeof initValue == 'object' ? initValue : {}
    } */
  });

  const modelSelectField = schema?.modelSelectField;
  const transformFields = useCallback(() => {
    const selectField: string = modelSelectField || "";
    if (!selectField) return "id,name";
    return selectField
      .split(",")
      .map((item) => {
        const [key] = item.split("$$");
        return key;
      })
      .join(",");
  }, [modelSelectField]);

  const fetchItemName = useCallback(
    async (_pageInfo: any, _schema: any, _output: any) => {
      if (
        !_pageInfo ||
        !_schema ||
        !_output ||
        (Array.isArray(_output) && _output.length === 0)
      ) {
        return;
      }
      const filter: Record<string, any> = {};

      if (Array.isArray(_output)) {
        filter.id = _output;
      } else {
        filter.id = _output;
      }
      const embed = _schema.embed || embeds;
      for (let item of embed) {
        if (typeof item == "object") {
          Object.keys(item).forEach((key) => {
            filter[key] = item[key];
          });
        }
      }
      try {
        const rs: any = await helper.callPageApi(_pageInfo, _schema.api, {
          queryInput: JSON.stringify(filter),
          select: transformFields(),
          limit: 100, // Ensure we get all requested items
        });
        const uniqueNames = uniqBy(rs?.data?.data || [], "id");
        const display: Array<any> = [];
        uniqueNames.map((d: Record<string, any>) => {
          return display.push(d.name || d.title || d.id);
        });
        setDisplayObj({ names: uniqueNames, display: join(display, "-") });
      } catch (err) {
        if (IS_DEBUG) {
          console.log(
            `🚀 ~ file: ArraySelect.tsx ~ line 60 ~ fetchItemName ~ err`,
            err,
          );
        }
      }
    },
    [embeds, transformFields],
  );

  const handleVisibleChange = () => {
    setVisible(!visible);
  };

  const onChange = useCallback(
    (keys: Array<number>, rows: Array<Record<string, any>>) => {
      setValue(keys);
      setDisplayObj((prev) => ({
        ...prev,
        names: uniqBy(rows || [], "id"),
      }));
      if (type === "checkbox") {
        superChange?.(keys);
      } else {
        superChange?.(keys[0]);
      }
    },
    [type, superChange],
  );

  const onRemoveClick = useCallback(
    (id: number) => {
      if (IS_DEBUG) {
        console.log(
          `🚀 ~ file: ArraySelect.tsx ~ line 89 ~ onRemoveClick ~ id`,
          id,
        );
      }

      setDisplayObj((prev) => {
        const output = [];
        const _names: Array<any> = [];
        for (let i = 0; i < prev.names.length; i++) {
          if (prev.names[i].id !== id) {
            output.push(prev.names[i].id);
            _names.push(prev.names[i]);
          }
        }
        setValue(output);

        // Call onChange directly without dependency on onChange
        if (type === "checkbox") {
          superChange?.(output);
        } else {
          superChange?.(output[0]);
        }

        return {
          ...prev,
          names: _names,
          display: join(
            _names.map((n: any) => n.name || n.title),
            "-",
          ),
        };
      });
    },
    [type, superChange],
  );

  const renderNames = useCallback(() => {
    const _renderTags = (items: Array<any>) => (
      <Row
        className="array-select__tag-list"
        style={{ marginLeft: 0, marginRight: 0 }}
      >
        {items.map((item: any) => {
          const label = String(item.name || item.title || item.id);

          return (
            <Col className="array-select__tag-column" key={`col-${item.id}`}>
              <Tag
                className="array-select__tag"
                key={`tag-${item.id}`}
                closable={!rest.disabled}
                // color={COLORS[random(11)]}
                onClose={() => {
                  onRemoveClick(item.id);
                }}
              >
                <span className="array-select__tag-label">{label}</span>
              </Tag>
            </Col>
          );
        })}
      </Row>
    );
    const _tags = _renderTags(displayObj.names);
    if (displayObj.names.length === 0) return null;
    return <React.Fragment>{_tags}</React.Fragment>;
  }, [displayObj.names, rest.disabled, onRemoveClick]);

  // Memoize schema properties to prevent unnecessary re-initialization
  const currentSchemaPageId = schema?.pageId;
  const currentSchemaApi = schema?.api;
  const currentSchemaEmbed = schema?.embed;
  const currentValue = props.value;

  const schemaPageId = useMemo(
    () => currentSchemaPageId,
    [currentSchemaPageId],
  );
  const schemaApi = useMemo(() => currentSchemaApi, [currentSchemaApi]);
  const schemaEmbed = useMemo(() => currentSchemaEmbed, [currentSchemaEmbed]);

  // Sync internal value with props.value
  useEffect(() => {
    if (currentValue !== undefined && !isEqual(currentValue, value)) {
      if (Array.isArray(currentValue)) {
        setValue(currentValue);
      } else {
        setValue([currentValue]);
      }
    }
  }, [currentValue, value]);

  // Fetch item names when value changes and we have pageInfo
  useEffect(() => {
    if (pageInfo && value && Array.isArray(value) && value.length > 0) {
      // Logic check: if already have enough names in displayObj.names, do not fetch again
      const currentNameIds = displayObj.names.map((n) => String(n.id));
      const hasMissingNames = value.some(
        (id) => !currentNameIds.includes(String(id)),
      );
      const hasDifferentLength = value.length !== displayObj.names.length;

      const fetchKey = JSON.stringify([...value].sort());
      if (
        (hasMissingNames || hasDifferentLength) &&
        lastFetchRef.current !== fetchKey
      ) {
        lastFetchRef.current = fetchKey;
        fetchItemName(
          pageInfo,
          {
            pageId: schemaPageId,
            api: schemaApi,
            embed: schemaEmbed,
            modelSelectField: schema?.modelSelectField,
          },
          [...value],
        );
      }
    } else if ((!value || value.length === 0) && displayObj.names.length > 0) {
      setDisplayObj({ names: [], display: "" });
      lastFetchRef.current = "";
    }
  }, [
    pageInfo,
    value,
    fetchItemName,
    schemaPageId,
    schemaApi,
    schemaEmbed,
    displayObj.names,
    modelSelectField,
  ]);

  useEffect(() => {
    const initialize = async () => {
      if (!schemaPageId) return;

      const _pageInfo = await helper.getPage(schemaPageId);
      setPageInfo(_pageInfo);
      setEmbeds(schemaEmbed || []);
    };

    initialize();
  }, [schemaPageId, schemaEmbed]);

  const overlay = useMemo(
    () =>
      type === "checkbox" ? (
        <ArrayTable
          pageInfo={pageInfo}
          schema={schema}
          value={value}
          selectedRows={displayObj.names}
          onChange={onChange}
          disabled={rest.disabled}
          itemsPerPage={5}
          type={type}
        />
      ) : (
        <ArrayTable
          pageInfo={pageInfo}
          schema={schema}
          value={value}
          selectedRows={displayObj.names}
          onChange={onChange}
          disabled={rest.disabled}
          itemsPerPage={5}
          type={"radio"}
        />
      ),
    [type, pageInfo, schema, value, onChange, rest.disabled, displayObj.names],
  );

  return (
    <Dropdown
      overlayStyle={{
        minWidth: "450px",
      }}
      overlay={overlay}
      trigger={["click"]}
      placement="bottomLeft"
      onVisibleChange={handleVisibleChange}
      visible={visible}
      disabled={rest.disabled}
    >
      <div
        className={`gx-dropdown-content ${rest.className || ""}`}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          border: "1px solid #d9d9d9",
          borderRadius: 6,
          padding: "4px 11px",
          minHeight: 32,
          backgroundColor: rest.disabled ? "#f5f5f5" : "#fff",
          cursor: rest.disabled ? "not-allowed" : "pointer",
          width: "100%",
          boxSizing: "border-box",
          ...rest.style,
        }}
      >
        <div
          className="array-select__values"
          style={{
            flex: 1,
            minWidth: 0,
          }}
        >
          {type === "checkbox" ? (
            value && value.length > 0 ? (
              renderNames()
            ) : (
              <span style={{ color: "#bfbfbf" }}>
                {placeholder || "Chọn mục"}
              </span>
            )
          ) : value && value.length > 0 ? (
            renderNames()
          ) : (
            <span style={{ color: "#bfbfbf" }}>
              {placeholder || "Chọn mục"}
            </span>
          )}
        </div>
        <button
          type="button"
          className="ant-dropdown-link"
          onClick={(e) => {
            e.preventDefault();
          }}
          style={{
            background: "none",
            border: "none",
            padding: "0 0 0 8px",
            flex: "0 0 auto",
            cursor: rest.disabled ? "not-allowed" : "pointer",
            display: "flex",
            alignItems: "center",
          }}
        >
          <DownOutlined style={{ color: "#1DA57A" }} />
        </button>
      </div>
    </Dropdown>
  );
};

export default ArraySelect;
